/**
 * /checkin/admin/conversations — Conversation Intelligence Center.
 *
 * Super-Admin only. Zero transcript text unless the admin explicitly
 * confirms viewing. Executive intelligence layout, not messaging.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CHECKIN_PALETTE as P } from "../_shell";
import { getToken } from "@/lib/checkinApi";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const BASE = `${BACKEND}/api/kcis/admin/intelligence`;

async function api(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method || "GET",
    headers: { "Content-Type": "application/json",
               Authorization: `Bearer ${getToken()}` },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let d = null; try { d = (await res.json()).detail; } catch {}
    const e = new Error(typeof d === "string" ? d : (d?.code || `HTTP ${res.status}`));
    e.status = res.status; e.detail = d; throw e;
  }
  return res.json();
}

const TABS = [
  ["dashboard", "Dashboard"], ["conversations", "Conversations"],
  ["escalations", "Escalations"], ["audit", "Audit"],
];

export default function ConversationIntelligence() {
  const navigate = useNavigate();
  const [gate, setGate] = useState("loading");
  const [tab, setTab] = useState("dashboard");
  const [privacyMode, setPrivacyMode] = useState(true);

  useEffect(() => {
    if (!getToken()) { setGate("signin"); return; }
    api("/providers")
      .then(() => setGate("ok"))
      .catch((e) => setGate(e.status === 403 ? "forbidden" : "error"));
  }, []);

  if (gate === "loading") return <FS>Checking Super Admin access…</FS>;
  if (gate === "signin") return <FS>
    <Link to="/checkin/admin/signin?next=/checkin/admin/conversations"
          className="px-3 py-2 rounded-lg text-white text-sm"
          style={{ background: P.purple }}
          data-testid="intel-signin-link">Sign in as Super Admin</Link>
  </FS>;
  if (gate === "forbidden") return <FS>
    <div className="text-center" data-testid="intel-forbidden">
      <div className="text-2xl font-semibold mb-2">403 Forbidden</div>
      <div className="text-sm" style={{ color: "#5F5766" }}>
        The Conversation Intelligence Center is restricted to the Super Administrator.
      </div>
    </div>
  </FS>;
  if (gate === "error") return <FS>Something went wrong loading the workspace.</FS>;

  return (
    <div style={{ background: P.warmBg, color: P.ink, minHeight: "100vh" }}
         data-testid="intel-workspace">
      <div className="border-b" style={{ background: "#FFFFFF", borderColor: P.silverSoft }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: P.purple }}>
              KCIS Admin · Check In · Super Admin
            </div>
            <div className="text-xl font-semibold mt-1">Conversation Intelligence Center</div>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-xs flex items-center gap-1"
                    style={{ color: "#5F5766" }}
                    data-testid="intel-privacy-toggle">
              <input type="checkbox" checked={privacyMode}
                      onChange={(e) => setPrivacyMode(e.target.checked)} />
              Privacy Mode
            </label>
            <Link to="/checkin/admin" className="text-xs underline" style={{ color: P.purple }}>
              ← Admin
            </Link>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 pb-16">
        <div className="mt-4 flex gap-2 border-b" style={{ borderColor: P.silverSoft }}
             data-testid="intel-tabs">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
                    data-testid={`intel-tab-${id}`}
                    className="px-4 py-2 text-sm"
                    style={{ borderBottom: tab === id ? `2px solid ${P.purple}` : "2px solid transparent",
                              color: tab === id ? P.ink : "#6B6373",
                              fontWeight: tab === id ? 600 : 400 }}>{label}</button>
          ))}
        </div>
        {tab === "dashboard" && <Dashboard />}
        {tab === "conversations" && <ConversationsTab privacyMode={privacyMode}
                                                         onOpen={(c) => navigate(`/checkin/admin/conversations/${c.id}`)} />}
        {tab === "escalations" && <EscalationsTab />}
        {tab === "audit" && <AuditTab />}
      </div>
    </div>
  );
}

function FS({ children }) {
  return <div style={{ background: P.warmBg, color: P.ink, minHeight: "100vh" }}
              className="flex items-center justify-center px-6">{children}</div>;
}

function Tile({ label, value, tone, testid }) {
  return (
    <div className="rounded-2xl p-3" data-testid={testid}
         style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}>
      <div className="text-[11px] uppercase tracking-widest" style={{ color: "#9E97AB" }}>{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ color: tone || P.ink }}>{value ?? 0}</div>
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api("/dashboard?preset=30d").then(setData).catch(() => {}); }, []);
  if (!data) return <div className="mt-6 text-sm" style={{ color: "#5F5766" }}>Loading…</div>;
  const a = data.totals?.activity || {}, r = data.totals?.relationship || {}, ai = data.totals?.ai_intelligence || {};
  return (
    <div className="mt-6 space-y-6" data-testid="intel-dashboard">
      <Section title="Activity">
        <Tile label="Active conversations" value={a.active_conversations} testid="tile-active" />
        <Tile label="Today" value={a.conversations_today} testid="tile-today" />
        <Tile label="This week" value={a.conversations_this_week} testid="tile-week" />
        <Tile label="Avg duration (sec)" value={a.average_conversation_duration_seconds} testid="tile-avg-dur" />
        <Tile label="Avg substantive user msgs" value={a.avg_substantive_user_messages} testid="tile-avg-user" />
        <Tile label="Avg substantive Dr. C" value={a.avg_substantive_dr_c_responses} testid="tile-avg-drc" />
      </Section>
      <Section title="Relationship Metrics">
        <Tile label="New users" value={r.new_users} testid="tile-new" />
        <Tile label="Returning users" value={r.returning_users} testid="tile-return" />
        <Tile label="Membership conversions" value={r.membership_conversions} testid="tile-mem" />
        <Tile label="Talk-credit conversions" value={r.talk_credit_conversions} testid="tile-tc" />
        <Tile label="Avg Trust Score (internal)" value={r.average_trust_score} tone={P.purple} testid="tile-trust" />
      </Section>
      <Section title="AI Intelligence">
        <Tile label="AI summaries" value={ai.ai_summaries_generated} testid="tile-summ" />
        <Tile label="Suggested Next Check-Ins" value={ai.suggested_next_checkins_generated} testid="tile-next" />
        <Tile label="Journey entries" value={ai.journey_timeline_entries} testid="tile-journey" />
        <Tile label="Relationship memory entries" value={ai.relationship_memory_entries} testid="tile-memory" />
      </Section>
      <Section title="Follow-Up Queue">
        {(data.follow_up_queue || []).length === 0 && (
          <div className="col-span-full text-sm p-3 rounded-xl"
               style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: "#5F5766" }}>
            No open follow-ups.
          </div>
        )}
        {(data.follow_up_queue || []).map((f) => (
          <div key={f.id} className="rounded-2xl p-3 text-sm"
               style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
               data-testid={`follow-up-${f.id}`}>
            <div className="text-[11px] uppercase" style={{ color: P.purple }}>{f.kind}</div>
            <div className="mt-1">{f.reason || "—"}</div>
          </div>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest mb-2" style={{ color: P.purple }}>{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

function ConversationsTab({ privacyMode, onOpen }) {
  const [text, setText] = useState("");
  const [state, setState] = useState("");
  const [access, setAccess] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (text) params.set("text", text);
    if (state) params.set("state", state);
    if (access) params.set("access_type", access);
    api(`/conversations?${params.toString()}`).then(setData).catch(() => {});
  }, [text, state, access]);
  useEffect(() => { load(); }, [load]);
  const blur = (s) => privacyMode ? "•".repeat(Math.min(28, (s || "").length || 4)) : s;
  return (
    <div className="mt-6 space-y-4" data-testid="intel-conversations">
      <div className="flex flex-wrap gap-2 items-center">
        <input value={text} onChange={(e) => setText(e.target.value)}
               placeholder="Search name / email / phone / user id / conversation id"
               data-testid="intel-search-input"
               className="px-3 py-2 rounded-lg text-sm flex-1"
               style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }} />
        <select value={state} onChange={(e) => setState(e.target.value)}
                data-testid="intel-filter-state"
                className="px-2 py-2 rounded-lg text-sm"
                style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }}>
          <option value="">All states</option>
          <option value="active">Active</option>
          <option value="paused_awaiting_paid">Paused</option>
          <option value="closed">Closed</option>
        </select>
        <select value={access} onChange={(e) => setAccess(e.target.value)}
                data-testid="intel-filter-access"
                className="px-2 py-2 rounded-lg text-sm"
                style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: P.ink }}>
          <option value="">All access</option>
          <option value="complimentary">Complimentary</option>
          <option value="talk_credit">Talk Credit</option>
          <option value="membership">Membership</option>
        </select>
      </div>
      <div className="rounded-2xl overflow-hidden"
           style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}>
        {(data?.items || []).length === 0 && (
          <div className="p-6 text-center text-sm" style={{ color: "#5F5766" }}
               data-testid="intel-empty">No conversations match.</div>
        )}
        {(data?.items || []).map((c) => (
          <button key={c.id} onClick={() => onOpen(c)}
                  data-testid={`intel-row-${c.id}`}
                  className="w-full text-left px-4 py-3 border-b block"
                  style={{ borderColor: P.silverSoft }}>
            <div className="flex justify-between text-sm">
              <div>
                <div className="font-medium">{c.user_display} · {c.phone_masked || "—"}</div>
                <div className="text-xs" style={{ color: "#5F5766" }}>
                  {blur(c.summary_title || "(no summary)")} · {(c.themes || []).join(" · ")}
                </div>
              </div>
              <div className="text-right text-[11px]" style={{ color: "#9E97AB" }}>
                {(c.created_at || "").slice(0, 10)} · {c.state} · trust {c.trust_score}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function EscalationsTab() {
  const [data, setData] = useState(null);
  const load = () => api("/escalations").then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, action, destination) => {
    const notes = window.prompt("Notes (optional):") || "";
    await api(`/escalations/${id}/action`,
      { method: "POST", body: { action, notes, destination } });
    load();
  };
  return (
    <div className="mt-6 space-y-3" data-testid="intel-escalations">
      {(data?.items || []).length === 0 && (
        <div className="text-sm p-3 rounded-xl"
             style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}`, color: "#5F5766" }}
             data-testid="intel-esc-empty">No escalations yet.</div>
      )}
      {(data?.items || []).map((e) => (
        <div key={e.id} className="rounded-2xl p-3 text-sm"
             style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
             data-testid={`intel-esc-${e.id}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[10px] uppercase" style={{ color: P.purple }}>{e.kind} · {e.priority}</div>
              <div className="mt-1 font-medium">{e.reason || "—"}</div>
              <div className="text-[11px]" style={{ color: "#9E97AB" }}>
                {(e.created_at || "").slice(0, 10)} · {e.status}
              </div>
            </div>
            <div className="flex gap-1 flex-wrap justify-end">
              <ActBtn onClick={() => act(e.id, "mark_reviewed")}>Reviewed</ActBtn>
              <ActBtn onClick={() => act(e.id, "assign_to_dr_christy")}>Assign to Dr. C</ActBtn>
              <ActBtn onClick={() => act(e.id, "schedule_follow_up")}>Follow-up</ActBtn>
              <ActBtn onClick={() => {
                const dest = window.prompt("Destination? coaching | clinic | university | comfort_care | other");
                if (dest) act(e.id, "convert_to_lead", dest);
              }}>Convert to lead</ActBtn>
              <ActBtn onClick={() => act(e.id, "dismiss")} tone="danger">Dismiss</ActBtn>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActBtn({ onClick, children, tone }) {
  return (
    <button onClick={onClick}
            className="text-[11px] px-2 py-1 rounded-md"
            style={{ background: tone === "danger" ? "#FEEEF1" : P.silverSoft,
                      color: tone === "danger" ? P.danger : P.ink }}>
      {children}
    </button>
  );
}

function AuditTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api("/audit?limit=200").then(setData).catch(() => {}); }, []);
  return (
    <div className="mt-6 rounded-2xl overflow-hidden"
         style={{ background: "#FFFFFF", border: `1px solid ${P.silverSoft}` }}
         data-testid="intel-audit-table">
      {(data?.entries || []).length === 0 && (
        <div className="p-6 text-sm text-center" style={{ color: "#5F5766" }}
             data-testid="intel-audit-empty">No audit entries.</div>
      )}
      {(data?.entries || []).map((e) => (
        <div key={e.id} className="px-4 py-2 border-b text-xs"
             style={{ borderColor: P.silverSoft }}
             data-testid={`intel-audit-${e.id}`}>
          <div><b>{e.administrator_email}</b> · {e.created_at?.slice(0, 19)}</div>
          <div style={{ color: "#5F5766" }}>
            conv {e.conversation_id?.slice(0, 8)} · reason: {e.reason || "—"}
            {e.message_count != null && <> · msgs: {e.message_count}</>}
          </div>
        </div>
      ))}
    </div>
  );
}
